import React, {
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import uuid from 'uuid-random';
import { useSelector } from 'react-redux';

import { requestStatusCheckers } from '../../../redux/helpers';
import { ClaudeSelectors } from '../../../redux/selectors';
import HideIcon from '../../../../assets/arrow-collapse.svg';
import { Identity, GenerationType } from '../../../../../shared/enums';

import Chat from './Chat';
import MessageThread from './MessageThread';
import FilesList from './FilesList';

import styles from '../../../styles/views/components/AIChatbot/GenerateWindow.module.scss';

const GenerateWindow = ({
  currentUser,
  generationType,
  files,
  onHide,
  chapterData,
  nextChapterData,
  prompts,
}) => {
  const messagesContainerRef = useRef(null);
  const claudeRequestStatus = useSelector(ClaudeSelectors.selectRequestStatus);
  const answer = useSelector(ClaudeSelectors.selectClaudeAnswer);
  const [messageThread, setMessageThread] = useState([]);
  const [isTranscriptIncluded, setIsTranscriptIncluded] = useState(true);

  const tokensMultiplier = useMemo(() => (generationType === GenerationType.OUTLINE ? 0.85 : 1), [generationType]); // Save 15% for tag wrapper on outline

  const handleTranscriptIncludedToggle = useCallback(() => setIsTranscriptIncluded(prev => !prev), []);

  const handleSendHumanMessage = useCallback((messageText) => {
    setMessageThread([
      ...messageThread,
      { id: uuid(), type: Identity.HUMAN, messageText },
    ]);
  }, [messageThread]);

  const isAnswerLoading = useMemo(() => requestStatusCheckers.isLoading(claudeRequestStatus), [claudeRequestStatus]);

  useEffect(() => {
    setMessageThread([
      ...messageThread,
      answer,
    ]);
  }, [answer]); // eslint-disable-line react-hooks/exhaustive-deps

  const mapPageNames = {
    [GenerationType.OUTLINE]: t('components.outline', { ns: i18next.Namespace.COMMON }),
    [GenerationType.STYLE_SAMPLES]: t('components.styleSamples', { ns: i18next.Namespace.COMMON }),
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleWrapper}>
          <p className={styles.text}>{t('components.windowTitle', { pageName: mapPageNames[generationType], ns: i18next.Namespace.COMMON })}</p>
          <div className={styles.claude}>
            <span>{t('components.claudeOpus', { ns: i18next.Namespace.COMMON })}</span>
          </div>
        </div>
        <HideIcon className={styles.hideIcon} onClick={onHide} />
      </div>
      <div className={styles.content}>
        <div className={styles.listWrapper} ref={messagesContainerRef}>
          <FilesList files={files} />
          <MessageThread
            messagesContainerRef={messagesContainerRef}
            currentUser={currentUser}
            messages={messageThread}
            isAnswerLoading={isAnswerLoading}
            isTranscriptIncluded={isTranscriptIncluded}
            onHide={onHide}
            generationType={generationType}
            tokensMultiplier={tokensMultiplier}
          />
        </div>
        <Chat
          onSend={handleSendHumanMessage}
          isAnswerLoading={isAnswerLoading}
          isTranscriptIncluded={isTranscriptIncluded}
          handleTranscriptIncludedToggle={handleTranscriptIncludedToggle}
          promptOptions={prompts}
          chapterData={chapterData}
          nextChapterData={nextChapterData}
          tokensMultiplier={tokensMultiplier}
          generationType={generationType}
        />
      </div>
    </div>
  );
};

GenerateWindow.propTypes = {
  prompts: PropTypes.array.isRequired,
  chapterData: PropTypes.object,
  nextChapterData: PropTypes.object,
  currentUser: PropTypes.object,
  generationType: PropTypes.string,
  files: PropTypes.array,
  onHide: PropTypes.func,
};

GenerateWindow.defaultProps = {
  chapterData: {},
  nextChapterData: {},
  currentUser: {},
  generationType: '',
  files: [],
  onHide: () => {},
};

export default GenerateWindow;
